import { useEffect, useRef } from 'react';
import { useGoogleMap } from '@react-google-maps/api';
import './SensorCanvasLayer.css';

export interface CanvasDot {
  latitude: number;
  longitude: number;
}

interface SensorCanvasLayerProps<T extends CanvasDot> {
  points: T[];
  selectedIndex: number | null;
  onPointClick: (index: number, point: T) => void;
  // Pulse the latest reading's rings. Off for finished shipments, where the
  // latest reading is no longer live and gets a static ring instead.
  pulseLatest?: boolean;
}

const DOT_RADIUS = 4;
const SELECTED_RADIUS = 8;
const LATEST_RADIUS = 7;
const CLICK_RADIUS = 14;
const DOT_COLOR = '#266EF1';

// Renders sensor dots on a single canvas overlay instead of one <Marker> per
// point. Handles thousands of dots at 60 fps with no React reconciliation cost.
// Click detection is done via the map's click event rather than per-marker
// listeners, so pointer-events on the canvas are disabled (map panning is
// never blocked).
//
// The latest reading's rings are a separate DOM element animated with CSS
// transform/opacity, so the pulse runs on the compositor and never forces a
// canvas redraw.
function SensorCanvasLayer<T extends CanvasDot>({
  points,
  selectedIndex,
  onPointClick,
  pulseLatest = true,
}: SensorCanvasLayerProps<T>): null {
  const map = useGoogleMap();

  // Keep mutable refs so the draw/click closures always see the latest values
  // without needing to be re-created on every render.
  const pointsRef = useRef(points);
  const selectedRef = useRef(selectedIndex);
  const onClickRef = useRef(onPointClick);
  const pulseLatestRef = useRef(pulseLatest);
  const redrawRef = useRef<() => void>(() => {});
  const overlayRef = useRef<google.maps.OverlayView | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  pointsRef.current = points;
  selectedRef.current = selectedIndex;
  onClickRef.current = onPointClick;
  pulseLatestRef.current = pulseLatest;

  // Create the overlay once when the map instance is available.
  useEffect(() => {
    if (!map) return;

    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.pointerEvents = 'none';

    const latestMarker = document.createElement('div');
    latestMarker.className = 'sensor-latest-marker';
    latestMarker.setAttribute('aria-hidden', 'true');
    latestMarker.style.display = 'none';
    for (const modifier of ['', 'sensor-latest-ring--delayed']) {
      const ring = document.createElement('span');
      ring.className = `sensor-latest-ring ${modifier}`.trim();
      latestMarker.appendChild(ring);
    }

    function draw() {
      const overlay = overlayRef.current;
      if (!overlay || !canvasRef.current) return;
      const projection = overlay.getProjection();
      if (!projection) return;

      const pts = pointsRef.current;
      const last = pts.length > 0 ? pts[pts.length - 1] : null;
      const lastPx = last
        ? projection.fromLatLngToDivPixel(
            new google.maps.LatLng(last.latitude, last.longitude)
          )
        : null;

      // Rings share the canvas's pane, so pane pixel coordinates apply as-is.
      latestMarker.style.display = lastPx ? '' : 'none';
      latestMarker.classList.toggle(
        'sensor-latest-marker--live',
        pulseLatestRef.current
      );
      if (lastPx) {
        latestMarker.style.transform = `translate(${lastPx.x}px, ${lastPx.y}px)`;
      }

      const bounds = map!.getBounds();
      if (!bounds) return;

      const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
      const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());
      if (!ne || !sw) return;

      const left = Math.min(ne.x, sw.x);
      const top = Math.min(ne.y, sw.y);
      const width = Math.ceil(Math.abs(ne.x - sw.x));
      const height = Math.ceil(Math.abs(sw.y - ne.y));
      if (width === 0 || height === 0) return;

      canvasRef.current.style.left = `${left}px`;
      canvasRef.current.style.top = `${top}px`;
      canvasRef.current.width = width;
      canvasRef.current.height = height;
      canvasRef.current.style.width = `${width}px`;
      canvasRef.current.style.height = `${height}px`;

      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);

      const lastIndex = pts.length - 1;
      // The latest dot is drawn after the loop so neighbouring dots can never
      // paint over it.
      for (let i = 0; i < lastIndex; i++) {
        const px = projection.fromLatLngToDivPixel(
          new google.maps.LatLng(pts[i].latitude, pts[i].longitude)
        );
        if (!px) continue;

        const cx = px.x - left;
        const cy = px.y - top;
        const isSelected = i === selectedRef.current;

        ctx.beginPath();
        ctx.arc(
          cx,
          cy,
          isSelected ? SELECTED_RADIUS : DOT_RADIUS,
          0,
          2 * Math.PI
        );
        ctx.fillStyle = DOT_COLOR;
        ctx.fill();
        ctx.strokeStyle = isSelected ? '#FFFFFF' : DOT_COLOR;
        ctx.lineWidth = isSelected ? 3 : 2;
        ctx.stroke();
      }

      if (lastPx) {
        const isSelected = lastIndex === selectedRef.current;
        ctx.beginPath();
        ctx.arc(
          lastPx.x - left,
          lastPx.y - top,
          isSelected ? SELECTED_RADIUS : LATEST_RADIUS,
          0,
          2 * Math.PI
        );
        ctx.fillStyle = DOT_COLOR;
        ctx.fill();
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = isSelected ? 3 : 2.5;
        ctx.stroke();
      }
    }

    redrawRef.current = draw;

    class CanvasOverlay extends google.maps.OverlayView {
      onAdd() {
        canvasRef.current = canvas;
        const pane = this.getPanes()!.overlayLayer;
        pane.appendChild(canvas);
        pane.appendChild(latestMarker);
      }
      draw() {
        redrawRef.current();
      }
      onRemove() {
        canvas.remove();
        latestMarker.remove();
        canvasRef.current = null;
      }
    }

    const overlay = new CanvasOverlay();
    overlay.setMap(map);
    overlayRef.current = overlay;

    // Detect which dot was clicked by translating the map click lat/lng to
    // pixel space and finding the nearest dot within CLICK_RADIUS px.
    const clickListener = map.addListener(
      'click',
      (e: google.maps.MapMouseEvent) => {
        if (!e.latLng || !overlayRef.current) return;
        const projection = overlayRef.current.getProjection();
        if (!projection) return;

        const clickPx = projection.fromLatLngToDivPixel(e.latLng);
        if (!clickPx) return;

        const pts = pointsRef.current;
        let bestIdx = -1;
        let bestDist = CLICK_RADIUS;

        // Resolve distance ties from the topmost dot, which is drawn last.
        for (let i = pts.length - 1; i >= 0; i--) {
          const px = projection.fromLatLngToDivPixel(
            new google.maps.LatLng(pts[i].latitude, pts[i].longitude)
          );
          if (!px) continue;
          const d = Math.hypot(px.x - clickPx.x, px.y - clickPx.y);
          if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
          }
        }

        if (bestIdx >= 0) {
          onClickRef.current(bestIdx, pts[bestIdx]);
        }
      }
    );

    return () => {
      overlay.setMap(null);
      google.maps.event.removeListener(clickListener);
      overlayRef.current = null;
    };
  }, [map]);

  // Redraw whenever points or selection changes between React renders.
  useEffect(() => {
    redrawRef.current();
  }, [points, selectedIndex, pulseLatest]);

  return null;
}

export default SensorCanvasLayer;
